import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "./SettingsPage.css";
import { Eye, EyeOff, Sun, Moon, Trash2 } from "lucide-react";

// PHASE 2 API BASE STARTS HERE
const API_BASE =
  process.env.REACT_APP_API_BASE ||
  (process.env.NODE_ENV === "production" ? "" : "http://localhost:5000");
// ENDS HERE

// ============================================================
// GRANULAR PERMISSIONS — UI ONLY FOR NOW, NOT WIRED TO ANY
// BACKEND OR ENFORCEMENT YET. Kept here as the single source of
// truth for the full role list discussed, so nothing gets lost
// before the actual enforcement gets built once the relevant
// pages (FillDgf, PropertyList, PropertyDetails, PropertyRevenue,
// ViewTargets, Dashboard/HomePage) are shared.
//
// Full role list + rules, for reference when wiring this up later:
//
// 1. READ FILL DGF — can fill the form and navigate every tab, but
//    the Submit button on the FAQ tab must be disabled. Read-only.
// 2. READ & WRITE FILL DGF — same as #1, but Submit is enabled.
// 3. READ PROPERTY LIST — can see the Property List and its Actions
//    column (view details), but NO delete option, and no ability to
//    edit property details at all.
// 4. READ & WRITE PROPERTY LIST — same as #3, but CAN delete and
//    edit property details.
// 5. READ PROPERTY-WISE TARGETS — can only see the Property Targets
//    List page, not the Revenue Calculator. Cannot delete a property
//    from that list. Clicking "View Targets" must NOT show the
//    pencil/edit option.
// 6. READ PROPERTY-WISE TARGETS & WRITE REVENUE CALCULATOR — same
//    as #5, but can also see AND submit the Revenue Calculator; on
//    the Property Targets List, CAN delete; and "View Targets" DOES
//    show the edit option.
// 7. READ DASHBOARDS — can view the Dashboards component on the
//    Home page.
//
// Prerequisites — each of the above requires its matching "SHOW"
// permission to be granted first, since these gate whether the
// section/tab is visible at all, before read/write access within
// it means anything:
// 8. SHOW FILL DGF          — prerequisite for #1 / #2
// 9. SHOW PROPERTIES LIST   — prerequisite for #3 / #4
// 10. SHOW RRC              — prerequisite for #5 / #6, with two
//     sub-variants: 10A SHOW RRC + SHOW REVENUE CALCULATOR, and
//     10B SHOW RRC + PROPERTY WISE TARGETS
// 11. SHOW DASHBOARDS       — prerequisite for #7
//
// Example: assigning "READ FILL DGF" to someone requires "SHOW FILL
// DGF" to already be assigned to them first — this dropdown does not
// yet enforce that check; it's purely a visual list-builder right now.
// ============================================================
const ADDITIONAL_PERMISSIONS = [
  { id: "show_fill_dgf", label: "SHOW FILL DGF" },
  { id: "read_fill_dgf", label: "READ FILL DGF" },
  { id: "read_write_fill_dgf", label: "READ & WRITE FILL DGF" },
  { id: "show_properties_list", label: "SHOW PROPERTIES LIST" },
  { id: "read_properties_list", label: "READ PROPERTY LIST" },
  { id: "read_write_properties_list", label: "READ & WRITE PROPERTY LIST" },
  { id: "show_rrc", label: "SHOW RRC" },
  { id: "show_rrc_revenue_calculator", label: "SHOW RRC + SHOW REVENUE CALCULATOR" },
  { id: "show_rrc_property_wise_targets", label: "SHOW RRC + PROPERTY WISE TARGETS" },
  { id: "read_property_wise_targets", label: "READ PROPERTY-WISE TARGETS" },
  { id: "read_targets_write_revenue_calc", label: "READ PROPERTY-WISE TARGETS & WRITE REVENUE CALCULATOR" },
  { id: "show_dashboards", label: "SHOW DASHBOARDS" },
  { id: "read_dashboards", label: "READ DASHBOARDS" },
];

// Which permissions each base role ALREADY effectively has, purely from
// existing role-based logic (FILL_DGF_ROLES, PROPERTY_LIST_ROLES/
// EDIT_ALLOWED_ROLES, REVENUE_CALCULATOR_ROLES, RRC_HOME_ROLES/
// PROPERTY_REVENUE_ROLES, DASHBOARD_ROLES across the app). Used only to
// highlight the dropdown below once a user is selected — this is purely
// informational, so an admin assigning extra access can see what's
// already covered by that user's role, and doesn't need to change based
// on anything actually stored anywhere.
const ROLE_BASELINE_PERMISSIONS = {
  "SUPER ADMIN": new Set(ADDITIONAL_PERMISSIONS.map((p) => p.id)),
  "ADMIN": new Set(ADDITIONAL_PERMISSIONS.map((p) => p.id)),
  "OTA MANAGER": new Set([
    "show_properties_list", "read_properties_list", "read_write_properties_list",
    "show_rrc", "show_rrc_revenue_calculator", "show_rrc_property_wise_targets",
    "read_property_wise_targets", "read_targets_write_revenue_calc",
    "show_dashboards", "read_dashboards",
  ]),
  "OTA EXECUTIVE": new Set([
    "show_properties_list", "read_properties_list",
    "show_rrc", "show_rrc_property_wise_targets", "read_property_wise_targets",
    "show_dashboards", "read_dashboards",
  ]),
  "SALES EXECUTIVE": new Set([
    "show_properties_list", "read_properties_list",
    "show_rrc", "show_rrc_property_wise_targets", "read_property_wise_targets",
    "show_dashboards", "read_dashboards",
  ]),
  "ONBOARDING MANAGER": new Set([
    "show_fill_dgf", "read_fill_dgf", "read_write_fill_dgf",
    "show_properties_list", "read_properties_list", "read_write_properties_list",
  ]),
  "ONBOARDING EXECUTIVE": new Set([
    "show_fill_dgf", "read_fill_dgf", "read_write_fill_dgf",
    "show_properties_list", "read_properties_list", "read_write_properties_list",
  ]),
};

// Renders one user's paginated log table (5 per page, single scrollable
// row of page numbers with Prev/Next arrows). Used both in the main Edit
// Logs section below AND inside the "View Logs" modal triggered from the
// Existing Users table — same component, same pagination state, so a
// page you're on in one place stays consistent if you open the other.
function UserLogTable({ userGroup, logPageByUser, setLogPageByUser, logsPerPage }) {
  if (userGroup.logs.length === 0) {
    return <p className="no-user-logs">No logs for this user.</p>;
  }

  const currentPageRaw = logPageByUser[userGroup.username] || 1;
  const totalPages = Math.ceil(userGroup.logs.length / logsPerPage);
  const currentPage = Math.min(currentPageRaw, totalPages);
  const startIdx = (currentPage - 1) * logsPerPage;
  const pageLogs = userGroup.logs.slice(startIdx, startIdx + logsPerPage);

  const setPage = (page) => {
    setLogPageByUser((prev) => ({ ...prev, [userGroup.username]: page }));
  };

  return (
    <>
      <table className="user-table">
        <thead>
          <tr>
            <th>Changes Made</th>
            <th>Who Made</th>
            <th>When Its Made</th>
          </tr>
        </thead>

        <tbody>
          {pageLogs.map((log, index) => (
            <tr key={log._id || index}>
              <td>{log.changeMade || "N/A"}</td>

              <td>
                {log.madeBy || "Unknown"}
                {log.madeByRole ? ` (${log.madeByRole})` : ""}
              </td>

              <td>
                {log.createdAt
                  ? new Date(log.createdAt).toLocaleString()
                  : "N/A"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {totalPages > 1 && (
        <div
          className="log-pagination"
          style={{
            display: "flex",
            alignItems: "center",
            gap: "6px",
            marginTop: "10px",
          }}
        >
          <button
            type="button"
            onClick={() => setPage(Math.max(1, currentPage - 1))}
            disabled={currentPage === 1}
            style={{
              flexShrink: 0,
              padding: "4px 10px",
              borderRadius: "4px",
              border: "1px solid #ccc",
              cursor: currentPage === 1 ? "default" : "pointer",
              backgroundColor: "#fff",
              color: currentPage === 1 ? "#ccc" : "#333",
            }}
          >
            ‹ Prev
          </button>

          <div
            style={{
              display: "flex",
              gap: "6px",
              overflowX: "auto",
              whiteSpace: "nowrap",
              padding: "2px",
            }}
          >
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNum) => (
              <button
                key={pageNum}
                type="button"
                onClick={() => setPage(pageNum)}
                style={{
                  flexShrink: 0,
                  padding: "4px 10px",
                  borderRadius: "4px",
                  border: "1px solid #ccc",
                  cursor: "pointer",
                  fontWeight: pageNum === currentPage ? 700 : 400,
                  backgroundColor: pageNum === currentPage ? "#1e293b" : "#fff",
                  color: pageNum === currentPage ? "#fff" : "#333",
                }}
              >
                {pageNum}
              </button>
            ))}
          </div>

          <button
            type="button"
            onClick={() => setPage(Math.min(totalPages, currentPage + 1))}
            disabled={currentPage === totalPages}
            style={{
              flexShrink: 0,
              padding: "4px 10px",
              borderRadius: "4px",
              border: "1px solid #ccc",
              cursor: currentPage === totalPages ? "default" : "pointer",
              backgroundColor: "#fff",
              color: currentPage === totalPages ? "#ccc" : "#333",
            }}
          >
            Next ›
          </button>
        </div>
      )}
    </>
  );
}

function SettingsPage() {
  const navigate = useNavigate();

  const loggedInUser = localStorage.getItem("loggedInUser");
  const loggedInRole = localStorage.getItem("loggedInRole");

  // ROLES PHASE 2 21 MAY 2026 STARTS HERE
  const ROLES = {
    SUPER_ADMIN: "SUPER ADMIN",
    ADMIN: "ADMIN",
    OTA_MANAGER: "OTA MANAGER",
    OTA_EXECUTIVE: "OTA EXECUTIVE",
    SALES_EXECUTIVE: "SALES EXECUTIVE",
    ONBOARDING_MANAGER: "ONBOARDING MANAGER",
    ONBOARDING_EXECUTIVE: "ONBOARDING EXECUTIVE",
  };

  const getCreatableRoles = (role) => {
    if (role === ROLES.SUPER_ADMIN) {
      return [
        ROLES.ADMIN,
        ROLES.OTA_MANAGER,
        ROLES.OTA_EXECUTIVE,
        ROLES.SALES_EXECUTIVE,
        ROLES.ONBOARDING_MANAGER,
        ROLES.ONBOARDING_EXECUTIVE,
      ];
    }

    if (role === ROLES.ADMIN) {
      return [
        ROLES.OTA_MANAGER,
        ROLES.OTA_EXECUTIVE,
        ROLES.SALES_EXECUTIVE,
        ROLES.ONBOARDING_MANAGER,
        ROLES.ONBOARDING_EXECUTIVE,
      ];
    }

    return [];
  };

  const canAccessSettings = [ROLES.SUPER_ADMIN, ROLES.ADMIN].includes(loggedInRole);
  const creatableRoles = getCreatableRoles(loggedInRole);
  const isSuperAdmin = loggedInRole === ROLES.SUPER_ADMIN;
  // const isAdmin = loggedInRole === ROLES.ADMIN;
  // ENDS HERE
// --- Redirect non-admins ---
useEffect(() => {
  if (!canAccessSettings) {
    alert("Access denied. Only Super Admin and Admin can access Settings.");
    navigate("/HomePage");
  }
}, [canAccessSettings, navigate]);
  
  const DEFAULT_ADMIN_EMAIL = "revx@hospitality2026.com";
  const DEFAULT_ADMIN_PASSWORD = "";

  // --- Load stored data ---
  const [theme, setTheme] = useState(localStorage.getItem("theme") || "light");
  const [formData, setFormData] = useState({
   username: localStorage.getItem("username") || loggedInUser || "Super Admin",
    email: localStorage.getItem("email") || DEFAULT_ADMIN_EMAIL,
    currentPassword: localStorage.getItem("adminPassword") || "",
  });

  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showCreateUserPassword, setShowCreateUserPassword] = useState(false);
  const [passwordData, setPasswordData] = useState({
    oldPassword: "",
    newPassword: "",
  });
const [newUser, setNewUser] = useState({
  username: "",
  password: "",
  role: "",
  // Needed for OTP login — every role except Super Admin/Admin now
  // requires an OTP as a second step alongside their password, sent to
  // this email.
  email: "",
});
  const [userList, setUserList] = useState([]); // store list of users
 const [visiblePasswords, setVisiblePasswords] = useState({});
const [editingPasswordUser, setEditingPasswordUser] = useState(null);
const [editedPasswords, setEditedPasswords] = useState({});
const [visibleEditedPasswords, setVisibleEditedPasswords] = useState({});
const [editLogs, setEditLogs] = useState([]);
// Which user's logs are currently shown in the "View Logs" modal — null
// means the modal is closed. Set from a row's View Logs button below.
const [viewLogsUser, setViewLogsUser] = useState(null);
// Edit-log pagination — tracked per user (each user's log table paginates
// independently), 5 logs per page. Keyed by username, defaults to page 1.
const LOGS_PER_PAGE = 5;
const [logPageByUser, setLogPageByUser] = useState({});

// --- Additional Permissions assignment table — now wired to the real
// backend routes (GET/PATCH /api/users/:username/permissions). userList
// (fetched below) already includes each user's additionalPermissions
// field, so assignedPermissions is derived from that rather than starting
// empty on every reload.
const [permAssignUsername, setPermAssignUsername] = useState("");
const [permAssignPermissionId, setPermAssignPermissionId] = useState("");
const [assignedPermissions, setAssignedPermissions] = useState([]); // [{ username, permissionId }]
const [savingPermission, setSavingPermission] = useState(false);

// --- Agent linking (Existing Users table, new "Link" column) ---
// Only meaningful for OTA EXECUTIVE / OTA MANAGER / SALES EXECUTIVE
// rows — links that user
// to one real agent name from properties-master, restricting every
// dashboard they view down to just that agent's properties (enforced
// server-side by agentFilter.js on every dashboard route).
const AGENT_LINKED_ROLES = [ROLES.OTA_MANAGER, ROLES.OTA_EXECUTIVE, ROLES.SALES_EXECUTIVE];
const [agentOptions, setAgentOptions] = useState([]);
const [savingAgentLinkFor, setSavingAgentLinkFor] = useState(null); // username currently saving, or null

const fetchAgentOptions = async () => {
  try {
    const res = await fetch(`${API_BASE}/api/properties/agents`);
    const data = await res.json();
    if (data.success) setAgentOptions(data.data || []);
  } catch (err) {
    console.error("Fetch agent options error:", err);
  }
};

const handleAgentLinkChange = async (username, linkedAgent) => {
  setSavingAgentLinkFor(username);
  try {
    const res = await fetch(
      `${API_BASE}/api/users/${encodeURIComponent(username)}/agent-link`,
      {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ linkedAgent, loggedInUser, loggedInRole }),
      }
    );
    const data = await res.json();
    if (!res.ok) throw new Error(data.message || "Failed to update agent link.");

    setUserList((prev) =>
      prev.map((u) => (u.username === username ? { ...u, linkedAgent: data.linkedAgent } : u))
    );
  } catch (err) {
    console.error("Agent link update error:", err);
    alert(err.message || "Failed to update agent link.");
  } finally {
    setSavingAgentLinkFor(null);
  }
};

// Sends this user's FULL updated permission list to the backend — the
// PATCH route replaces the whole array, so every call must include every
// permission this user should still have, not just the one that changed.
const savePermissionsForUser = async (username, permissionIds) => {
  const res = await fetch(
    `${API_BASE}/api/users/${encodeURIComponent(username)}/permissions`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ additionalPermissions: permissionIds }),
    }
  );

  const data = await res.json();

  if (!res.ok) {
    throw new Error(data.message || "Failed to update permissions.");
  }

  return data.additionalPermissions || permissionIds;
};

const handleAssignPermission = async (e) => {
  e.preventDefault();

  if (!permAssignUsername || !permAssignPermissionId) {
    alert("Please select both a user and a permission.");
    return;
  }

  const alreadyAssigned = assignedPermissions.some(
    (p) => p.username === permAssignUsername && p.permissionId === permAssignPermissionId
  );

  if (alreadyAssigned) {
    alert("This user already has that permission listed.");
    return;
  }

  const nextPermissionIds = [
    ...assignedPermissions
      .filter((p) => p.username === permAssignUsername)
      .map((p) => p.permissionId),
    permAssignPermissionId,
  ];

  setSavingPermission(true);
  try {
    await savePermissionsForUser(permAssignUsername, nextPermissionIds);

    setAssignedPermissions((prev) => [
      ...prev,
      { username: permAssignUsername, permissionId: permAssignPermissionId },
    ]);

    setPermAssignPermissionId("");
  } catch (err) {
    console.error("Assign permission error:", err);
    alert(err.message || "Failed to assign permission.");
  } finally {
    setSavingPermission(false);
  }
};

const handleRemoveAssignedPermission = async (username, permissionId) => {
  const nextPermissionIds = assignedPermissions
    .filter((p) => p.username === username && p.permissionId !== permissionId)
    .map((p) => p.permissionId);

  setSavingPermission(true);
  try {
    await savePermissionsForUser(username, nextPermissionIds);

    setAssignedPermissions((prev) =>
      prev.filter((p) => !(p.username === username && p.permissionId === permissionId))
    );
  } catch (err) {
    console.error("Remove permission error:", err);
    alert(err.message || "Failed to remove permission.");
  } finally {
    setSavingPermission(false);
  }
};

  // --- Load all users from localStorage ---
// --- Sync the real current password once users are loaded ---
// --- Load all users on mount ---
  useEffect(() => {
    const loadSettingsData = async () => {
      await fetchUsers();
      await fetchEditLogs();
      await fetchAgentOptions();
    };

    loadSettingsData();
  }, []);

  // Populate assignedPermissions from whatever each user already has
  // saved (userList's additionalPermissions field), whenever the user
  // list changes — e.g. right after fetchUsers() resolves on mount.
  useEffect(() => {
    const derived = [];
    userList.forEach((u) => {
      (u.additionalPermissions || []).forEach((permissionId) => {
        derived.push({ username: u.username, permissionId });
      });
    });
    setAssignedPermissions(derived);
  }, [userList]);

  // --- Sync the real current password once users are loaded ---
  useEffect(() => {
    if (!isSuperAdmin || !loggedInUser || userList.length === 0) return;

    const self = userList.find((u) => u.username === loggedInUser);

    if (self?.password && self.password !== formData.currentPassword) {
      setFormData((prev) => ({ ...prev, currentPassword: self.password }));
      localStorage.setItem("adminPassword", self.password);
    }
  }, [isSuperAdmin, loggedInUser, userList]); // eslint-disable-line react-hooks/exhaustive-deps

const fetchUsers = async () => {
  try {
    const res = await fetch(`${API_BASE}/api/users`);
    const data = await res.json();

    if (!res.ok) {
      throw new Error(data.message || "Failed to fetch users.");
    }

    const validUsers = Array.isArray(data)
      ? data.filter((user) => user && user.username)
      : [];

    setUserList(validUsers);
  } catch (err) {
    console.error("Fetch users error:", err);
    alert("Failed to load users.");
  }
};

  const fetchEditLogs = async () => {
  try {
    const res = await fetch(`${API_BASE}/api/edit-logs`);
    const data = await res.json();

    if (!res.ok) {
      throw new Error(data.message || "Failed to fetch edit logs.");
    }

    setEditLogs(Array.isArray(data) ? data : []);
  } catch (err) {
    console.error("Fetch edit logs error:", err);
    setEditLogs([]);
  }
};

  const handleClearLogs = async () => {
  if (!isSuperAdmin) {
    alert("Only Super Admin can clear edit logs.");
    return;
  }

  const confirmClear = window.confirm(
    "Are you sure you want to clear all edit logs? This action cannot be undone."
  );

  if (!confirmClear) return;

  try {
    const res = await fetch(`${API_BASE}/api/edit-logs`, {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        loggedInRole,
      }),
    });

    const data = await res.json();

    if (!res.ok) {
      throw new Error(data.message || "Failed to clear edit logs.");
    }

    alert("Edit logs cleared successfully.");
    await fetchEditLogs();
  } catch (err) {
    console.error("Clear logs error:", err);
    alert(err.message || "Failed to clear edit logs.");
  }
};
  
  const toggleUserPassword = (userKey) => {
  setVisiblePasswords((prev) => ({
    ...prev,
    [userKey]: !prev[userKey],
  }));
};

  const canResetUserPassword = (targetUser) => {
  if (!isSuperAdmin) return false;
  if (!targetUser || !targetUser.role) return false;

  return targetUser.role !== ROLES.SUPER_ADMIN;
};

const startEditUserPassword = (user) => {
  if (!canResetUserPassword(user)) {
    alert("You cannot edit this user's password.");
    return;
  }

  setEditingPasswordUser(user.username);
  setEditedPasswords((prev) => ({
    ...prev,
    [user.username]: "",
  }));
};

const cancelEditUserPassword = (username) => {
  setEditingPasswordUser(null);

  setEditedPasswords((prev) => ({
    ...prev,
    [username]: "",
  }));

  setVisibleEditedPasswords((prev) => ({
    ...prev,
    [username]: false,
  }));
};

const toggleEditedUserPassword = (username) => {
  setVisibleEditedPasswords((prev) => ({
    ...prev,
    [username]: !prev[username],
  }));
};

const handleResetUserPassword = async (user) => {
  if (!canResetUserPassword(user)) {
    alert("Only Super Admin can reset passwords for users below Super Admin role.");
    return;
  }

  const username = user.username;
  const newPassword = editedPasswords[username]?.trim();

  if (!newPassword) {
    alert("Please enter a new password.");
    return;
  }

  if (newPassword.length < 4) {
    alert("Password must be at least 4 characters.");
    return;
  }

  const confirmUpdate = window.confirm(
    `Are you sure you want to update password for '${username}'?`
  );

  if (!confirmUpdate) return;

  try {
    const res = await fetch(
      `${API_BASE}/api/users/${encodeURIComponent(username)}/reset-password`,
      {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          newPassword,
          loggedInUser,
          loggedInRole,
        }),
      }
    );

    const data = await res.json();

    if (!res.ok) {
      throw new Error(data.message || "Failed to update password.");
    }

    alert(`Password updated successfully for '${username}'.`);

    setEditingPasswordUser(null);

    setEditedPasswords((prev) => ({
      ...prev,
      [username]: "",
    }));

    setVisibleEditedPasswords((prev) => ({
      ...prev,
      [username]: false,
    }));

    await fetchUsers();
    await fetchEditLogs();
  } catch (err) {
    console.error("Reset user password error:", err);
    alert(err.message || "Failed to update password.");
  }
};
  // --- Save admin info ---
  useEffect(() => {
    localStorage.setItem("adminPassword", formData.currentPassword);
    localStorage.setItem("username", formData.username);
    localStorage.setItem("email", formData.email);
  }, [formData]);

  // --- Save theme ---
  useEffect(() => {
    localStorage.setItem("theme", theme);
    document.body.className = theme === "dark" ? "dark-theme" : "light-theme";
  }, [theme]);

  // --- Password Change ---
  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData((prev) => ({ ...prev, [name]: value }));
  };

 const handlePasswordSubmit = async (e) => {
  e.preventDefault();

  let { oldPassword, newPassword } = passwordData;

  oldPassword = oldPassword.trim();
  newPassword = newPassword.trim();

  if (!oldPassword || !newPassword) {
    alert("⚠️ Please fill both password fields.");
    return;
  }

  if (newPassword.length < 4) {
    alert("⚠️ New password must be at least 4 characters.");
    return;
  }

  try {
    const res = await fetch(`${API_BASE}/api/users/change-password`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        username: loggedInUser,
        role: loggedInRole,
        oldPassword,
        newPassword,
      }),
    });

    const data = await res.json();

    if (!res.ok) {
      throw new Error(data.message || "Failed to change password.");
    }

    setFormData((prev) => ({
  ...prev,
  username: loggedInUser,
  currentPassword: newPassword,
}));

localStorage.setItem("username", loggedInUser);
localStorage.setItem("adminPassword", newPassword);
    await fetchUsers();
    await fetchEditLogs();

    alert("✅ Password changed successfully!");
    setPasswordData({
      oldPassword: "",
      newPassword: "",
    });
  } catch (err) {
    console.error("Change password error:", err);
    alert(err.message || "Failed to change password.");
  }
};

  // --- Theme Toggle ---
  const toggleTheme = () => setTheme((prev) => (prev === "dark" ? "light" : "dark"));

 // --- Create User ---
const handleCreateUser = async (e) => {
  e.preventDefault();

  const username = newUser.username.trim();
  const password = newUser.password.trim();
  const role = newUser.role;

  if (!username || !password || !role) {
    alert("Please enter username, password, and role.");
    return;
  }

  if (password.length < 4) {
    alert("Password must be at least 4 characters.");
    return;
  }

  if (!creatableRoles.includes(role)) {
    alert("You are not allowed to create this role.");
    return;
  }

  const email = newUser.email.trim();

  try {
    const res = await fetch(`${API_BASE}/api/users`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        username,
        password,
        role,
        email,
        createdBy: loggedInUser,
        createdByRole: loggedInRole,
      }),
    });

    const data = await res.json();

    if (!res.ok) {
      throw new Error(data.message || "Failed to create user.");
    }

    alert(`User '${username}' created successfully as ${role}!`);

    setNewUser({
      username: "",
      password: "",
      role: "",
      email: "",
    });

     await fetchUsers();
     await fetchEditLogs();
  } catch (err) {
    console.error("Create user error:", err);
    alert(err.message || "Failed to create user.");
  }
};

  // PHASE 2 FUNCTION STARTS HERE
  const canDeleteUser = (targetUser) => {
  if (!targetUser || !targetUser.role) return false;

  if (loggedInRole === ROLES.SUPER_ADMIN) {
    return targetUser.role !== ROLES.SUPER_ADMIN;
  }

  if (loggedInRole === ROLES.ADMIN) {
    return ![ROLES.SUPER_ADMIN, ROLES.ADMIN].includes(targetUser.role);
  }

  return false;
};
  // ENDS HERE
  const handleDeleteUser = async (username) => {
  if (!username) {
    alert("Invalid user. Username missing.");
    return;
  }

  if (!window.confirm(`Are you sure you want to delete '${username}'?`)) {
    return;
  }

  try {
    const res = await fetch(
  `${API_BASE}/api/users/${encodeURIComponent(username)}`,
  {
    method: "DELETE",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
  loggedInUser,
  loggedInRole,
}),
  }
);

    const data = await res.json();

    if (!res.ok) {
      throw new Error(data.message || "Failed to delete user.");
    }

    alert(`User '${username}' deleted successfully.`);
    await fetchUsers();
    await fetchEditLogs();
  } catch (err) {
    console.error("Delete user error:", err);
    alert(err.message || "Failed to delete user.");
  }
};

  const getLogsGroupedByUser = () => {
  const users = Array.isArray(userList) ? userList : [];
  const logs = Array.isArray(editLogs) ? editLogs : [];

  const grouped = {};

  // First create one empty group for every existing user
  users.forEach((user) => {
    if (!user?.username) return;

    grouped[user.username] = {
      username: user.username,
      role: user.role || "",
      logs: [],
    };
  });

  // Also include Super Admin/current logged user if not already present
  if (loggedInUser && !grouped[loggedInUser]) {
    grouped[loggedInUser] = {
      username: loggedInUser,
      role: loggedInRole || "",
      logs: [],
    };
  }

  // Put each log inside the user who made it
  logs.forEach((log) => {
    const userKey = log.madeBy || "Unknown";

    if (!grouped[userKey]) {
      grouped[userKey] = {
        username: userKey,
        role: log.madeByRole || "",
        logs: [],
      };
    }

    grouped[userKey].logs.push(log);
  });

  return Object.values(grouped);
};
  
  return (
    <div className="settings-container">
      <h2 className="settings-title">⚙️ Settings</h2>
      <p className="user-role-info">
        Logged in as: <strong>{loggedInUser}</strong>{" "}
<span style={{ color: "#666" }}>({loggedInRole})</span>
      </p>

      {/* --- Theme Toggle --- */}
      <div className="theme-toggle">
        <button onClick={toggleTheme} className="theme-btn">
          {theme === "dark" ? (
            <>
              <Sun size={18} /> Light Mode
            </>
          ) : (
            <>
              <Moon size={18} /> Dark Mode
            </>
          )}
        </button>
      </div>

     {/* --- User Information: Super Admin only --- */}
      <div className="settings-grid">
{isSuperAdmin && (
  <div className="settings-section">
    <h3>User Information</h3>

    <div className="form-group">
      <label>Username</label>
      <input type="text" value={formData.username} readOnly />
    </div>

    <div className="form-group">
      <label>Email</label>
      <input type="email" value={formData.email} readOnly />
    </div>

    <div className="form-group password-view-group">
      <label>Current Password</label>
      <div className="password-view-wrapper">
        <input
          type={showCurrentPassword ? "text" : "password"}
          value={formData.currentPassword}
          readOnly
        />
        <span
          className="view-icon"
          onClick={() => setShowCurrentPassword((prev) => !prev)}
          title={showCurrentPassword ? "Hide Password" : "Show Password"}
        >
          {showCurrentPassword ? <EyeOff size={18} /> : <Eye size={18} />}
        </span>
      </div>
    </div>
  </div>
)}

    {/* --- Change Password: Super Admin only --- */}
{isSuperAdmin && (
  <div className="settings-section">
    <h3>Change Password</h3>

    <form onSubmit={handlePasswordSubmit}>
      <div className="form-group password-view-group">
        <label>Old Password</label>

        <div className="password-view-wrapper">
          <input
  type={showOldPassword ? "text" : "password"}
  name="oldPassword"
  autoComplete="off"
  value={passwordData.oldPassword}
  onChange={handlePasswordChange}
  placeholder="Enter old password"
/>

          <span
            className="view-icon"
            onClick={() => setShowOldPassword((prev) => !prev)}
            title={showOldPassword ? "Hide Password" : "Show Password"}
          >
            {showOldPassword ? <EyeOff size={18} /> : <Eye size={18} />}
          </span>
        </div>
      </div>

      <div className="form-group password-view-group">
        <label>New Password</label>

        <div className="password-view-wrapper">
         <input
  type={showNewPassword ? "text" : "password"}
  name="newPassword"
  autoComplete="new-password"
  value={passwordData.newPassword}
  onChange={handlePasswordChange}
  placeholder="Enter new password"
/>

          <span
            className="view-icon"
            onClick={() => setShowNewPassword((prev) => !prev)}
            title={showNewPassword ? "Hide Password" : "Show Password"}
          >
            {showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
          </span>
        </div>
      </div>

      <button type="submit" className="save-btn">
        🔒 Change Password
      </button>
    </form>
  </div>
)}

      {/* --- Create New User --- */}
      <div className="settings-section">
        <h3>Create New User</h3>
        <form onSubmit={handleCreateUser}>
          <div className="form-group">
            <label>Username</label>
            <input
  type="text"
  name="new-username"
  autoComplete="off"
  value={newUser.username}
  onChange={(e) =>
    setNewUser((prev) => ({ ...prev, username: e.target.value }))
  }
  placeholder="Enter new username"
/>
          </div>

          <div className="form-group password-view-group">
  <label>Password</label>

  <div className="password-view-wrapper">
   <input
  type={showCreateUserPassword ? "text" : "password"}
  name="new-user-password"
  autoComplete="new-password"
  value={newUser.password}
  onChange={(e) =>
    setNewUser((prev) => ({ ...prev, password: e.target.value }))
  }
  placeholder="Enter password"
/>

    <span
      className="view-icon"
      onClick={() => setShowCreateUserPassword((prev) => !prev)}
      title={showCreateUserPassword ? "Hide Password" : "Show Password"}
    >
      {showCreateUserPassword ? <EyeOff size={18} /> : <Eye size={18} />}
    </span>
  </div>
</div>

          {/* ROLE UI STARTS HERE */}
          <div className="form-group">
  <label>Role</label>
  <select
    value={newUser.role}
    onChange={(e) =>
      setNewUser((prev) => ({ ...prev, role: e.target.value }))
    }
  >
    <option value="">Select Role</option>

    {creatableRoles.map((role) => (
      <option key={role} value={role}>
        {role}
      </option>
    ))}
  </select>
</div>
          {/* ENDS HERE */}

          <button type="submit" className="save-btn">
            👤 Create User
          </button>
        </form>
      </div>

      {/* --- Additional Permissions assignment table — UI ONLY, no
          backend or enforcement wired up yet. See the ADDITIONAL_
          PERMISSIONS comment near the top of this file for the full
          rules for each permission, ready for when this gets wired up
          for real once the relevant pages are shared. --- */}
      <div className="settings-section existing-users-section">
        <h3>🔑 Assign Additional Permissions</h3>

        <form
          onSubmit={handleAssignPermission}
          style={{ display: "flex", gap: 12, alignItems: "flex-end", flexWrap: "wrap" }}
        >
          <div className="form-group" style={{ minWidth: 200, marginBottom: 0 }}>
            <label>User</label>
            <select
              value={permAssignUsername}
              onChange={(e) => setPermAssignUsername(e.target.value)}
            >
              <option value="">Select user</option>
              {userList
                .filter((u) => u && u.username)
                .map((u) => (
                  <option key={u._id || u.username} value={u.username}>
                    {u.username}
                  </option>
                ))}
            </select>
            {/* Invisible placeholder matching the hint text's height under
                the Permission/Role dropdown — keeps both columns the same
                height so this row stays aligned regardless of alignItems:
                flex-end below. */}
            <p style={{ fontSize: 11, marginTop: 4, minHeight: 14, visibility: "hidden" }}>
              &nbsp;
            </p>
          </div>

          <div className="form-group" style={{ minWidth: 320, marginBottom: 0 }}>
            <label>Permission / Role</label>
            <select
              value={permAssignPermissionId}
              onChange={(e) => setPermAssignPermissionId(e.target.value)}
            >
              <option value="">Select permission</option>
              {ADDITIONAL_PERMISSIONS.map((perm) => {
                const selectedUser = userList.find((u) => u.username === permAssignUsername);
                const baseline = selectedUser
                  ? ROLE_BASELINE_PERMISSIONS[selectedUser.role] || new Set()
                  : new Set();
                const alreadyHasViaRole = baseline.has(perm.id);

                return (
                  <option
                    key={perm.id}
                    value={perm.id}
                    style={
                      alreadyHasViaRole
                        ? { backgroundColor: "#dcfce7", color: "#166534", fontWeight: 700 }
                        : undefined
                    }
                  >
                    {alreadyHasViaRole ? `✓ ${perm.label} (already has via role)` : perm.label}
                  </option>
                );
              })}
            </select>
            <p
              style={{
                fontSize: 11,
                color: "#777",
                marginTop: 4,
                minHeight: 14,
                visibility: permAssignUsername ? "visible" : "hidden",
              }}
            >
              Green options are already covered by this user's existing role (
              {userList.find((u) => u.username === permAssignUsername)?.role || "—"}
              ).
            </p>
          </div>

          <div style={{ display: "flex", flexDirection: "column" }}>
            {/* Invisible label matching "User"/"Permission / Role" above,
                so the button itself lines up with the dropdowns — not
                just the columns' overall bottom edges. */}
            <label style={{ visibility: "hidden" }}>Assign</label>
            <button type="submit" className="save-btn" style={{ width: "auto", padding: "10px 24px" }} disabled={savingPermission}>
              {savingPermission ? "Saving..." : "Assign"}
            </button>
            <p style={{ fontSize: 11, marginTop: 4, minHeight: 14, visibility: "hidden" }}>
              &nbsp;
            </p>
          </div>
        </form>

        {assignedPermissions.length > 0 ? (
          <table className="user-table" style={{ marginTop: 20 }}>
            <thead>
              <tr>
                <th>User</th>
                <th>Permission / Role</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {assignedPermissions.map(({ username, permissionId }) => {
                const perm = ADDITIONAL_PERMISSIONS.find((p) => p.id === permissionId);
                return (
                  <tr key={`${username}-${permissionId}`}>
                    <td>{username}</td>
                    <td>{perm ? perm.label : permissionId}</td>
                    <td>
                      <button
                        type="button"
                        className="delete-btn"
                        onClick={() => handleRemoveAssignedPermission(username, permissionId)}
                        disabled={savingPermission}
                      >
                        <Trash2 size={16} /> Remove
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        ) : (
          <p style={{ color: "#777", marginTop: 16 }}>
            No additional permissions assigned yet.
          </p>
        )}
      </div>

        {/* --- List of Users --- */}
<div className="settings-section existing-users-section">
  <h3>👥 Existing Users</h3>
        {userList.length > 0 ? (
          <table className="user-table">
            <thead>
              <tr>
                <th>Username</th>
                {isSuperAdmin && <th>Password</th>}
                <th>Role</th>
                <th>Link</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
            {userList
  .filter((user) => user && user.username)
  .map((user, index) => (
    <tr key={user._id || user.username || index}>
  <td>{user.username}</td>

 {isSuperAdmin && (
  <td>
    {editingPasswordUser === user.username ? (
      <div className="table-password-edit-cell">
        <div className="table-password-input-wrapper">
          <input
            type={visibleEditedPasswords[user.username] ? "text" : "password"}
            value={editedPasswords[user.username] || ""}
            onChange={(e) =>
              setEditedPasswords((prev) => ({
                ...prev,
                [user.username]: e.target.value,
              }))
            }
            placeholder="New password"
            className="table-password-input"
          />

          <button
            type="button"
            className="table-password-toggle"
            onClick={() => toggleEditedUserPassword(user.username)}
            title={
              visibleEditedPasswords[user.username]
                ? "Hide password"
                : "Show password"
            }
          >
            {visibleEditedPasswords[user.username] ? (
              <EyeOff size={16} />
            ) : (
              <Eye size={16} />
            )}
          </button>
        </div>

        <div className="table-password-edit-actions">
          <button
            type="button"
            className="password-save-btn"
            onClick={() => handleResetUserPassword(user)}
          >
            Save
          </button>

          <button
            type="button"
            className="password-cancel-btn"
            onClick={() => cancelEditUserPassword(user.username)}
          >
            Cancel
          </button>
        </div>
      </div>
    ) : (
      <div className="table-password-cell">
        <span className="table-password-text">
          {visiblePasswords[user._id || user.username]
            ? user.password || "N/A"
            : "••••••••"}
        </span>

        <button
          type="button"
          className="table-password-toggle"
          onClick={() => toggleUserPassword(user._id || user.username)}
          title={
            visiblePasswords[user._id || user.username]
              ? "Hide password"
              : "Show password"
          }
        >
          {visiblePasswords[user._id || user.username] ? (
            <EyeOff size={16} />
          ) : (
            <Eye size={16} />
          )}
        </button>

        {canResetUserPassword(user) && (
          <button
            type="button"
            className="password-edit-btn"
            onClick={() => startEditUserPassword(user)}
          >
            Edit
          </button>
        )}
      </div>
    )}
  </td>
)}
  <td>{user.role || "N/A"}</td>

  <td>
    {AGENT_LINKED_ROLES.includes(user.role) ? (
      <select
        value={user.linkedAgent || ""}
        onChange={(e) => handleAgentLinkChange(user.username, e.target.value)}
        disabled={savingAgentLinkFor === user.username}
        style={{ minWidth: 140 }}
      >
        <option value="">Not linked</option>
        {agentOptions.map((agent) => (
          <option key={agent} value={agent}>
            {agent}
          </option>
        ))}
      </select>
    ) : (
      <span style={{ color: "#aaa" }}>—</span>
    )}
  </td>

  <td>
    <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
      <button
        type="button"
        className="delete-btn"
        style={{ backgroundColor: "#1d4ed8" }}
        onClick={() => setViewLogsUser(user.username)}
      >
        <Eye size={16} /> View Logs
      </button>

      {canDeleteUser(user) && (
        <button
          className="delete-btn"
          onClick={() => handleDeleteUser(user.username)}
        >
          <Trash2 size={16} /> Delete
        </button>
      )}
    </div>
  </td>
</tr>
  ))}
            </tbody>
          </table>
        ) : (
          <p style={{ color: "#777" }}>No users created yet.</p>
        )}
            </div>

        {/* --- Roles Description (guidelines) --- */}
        <div className="settings-section existing-users-section">
          <h3>📋 Roles Description</h3>
          <ul style={{ paddingLeft: 20, margin: 0, lineHeight: 1.7 }}>
            <li>
              <strong>SUPER ADMIN:</strong> Can create all types of users except
              another super admin, as there will be only one; also, this role
              has the right to delete every other user present.
            </li>
            <li>
              <strong>ADMIN:</strong> Can create users for the role as OTA
              Manager, OTA Executive, Sales Executive, Onboarding Manager, and
              Onboarding Executive only. Also, can delete all created users
              except Super Admin or Admin.
            </li>
            <li>
              <strong>OTA MANAGER:</strong> Can't create any user. They cannot
              fill any DGF form, as for them, the FILLDGF tab is disabled.
            </li>
            <li>
              <strong>OTA EXECUTIVE:</strong> Can't create any user. Also, for
              RRC, the user can only see property-wise targets, as they cannot
              set targets. Also, they are not allowed to fill out any DGF form.
            </li>
            <li>
              <strong>SALES EXECUTIVE:</strong> Currently, this role has the
              same rights as OTA Executive.
            </li>
            <li>
              <strong>ONBOARDING MANAGER:</strong> Also can't create any users,
              and this role is only allowed to view the property list or fill
              the DGF form.
            </li>
            <li>
              <strong>ONBOARDING EXECUTIVE:</strong> Currently, this role has
              the same rights as that of Onboarding Manager.
            </li>
          </ul>

           <h3>Features</h3>
          <ul style={{ paddingLeft: 20, margin: 0, lineHeight: 1.7 }}>
            <li>Except for the OTA Executive, every other role can edit DGF, as for this role, there is no option to edit or delete any document or any property form itself.</li>
            <li>Using this edit feature, one can easily edit the entire property details, including all string/text, remove or upload any documents, and reupload property room images</li>
            <li>Anyone with edit access can easily update all details, like adding more room categories that were missed earlier.</li>
            <li>Facilities, along with their charges, can be easily updated.</li>
            <li>For every user the session logs out every <strong>10 mins</strong> only if there is inactivity of mouse, keyboard key.</li>
          </ul>

           <h3>Additional Permissions Description</h3>
          <ul style={{ paddingLeft: 20, margin: 0, lineHeight: 1.7 }}>
            <li>
              <strong>SHOW FILL DGF:</strong> Prerequisite — makes the FILL
              DGF card/tab visible at all. Needed before either of the two
              FILL DGF permissions below mean anything.
            </li>
            <li>
              <strong>READ FILL DGF:</strong> Can fill the form and navigate
              every tab, but the Submit button on the FAQ tab is disabled —
              read-only, cannot actually submit.
            </li>
            <li>
              <strong>READ & WRITE FILL DGF:</strong> Same as above, but
              Submit is enabled.
            </li>
            <li>
              <strong>SHOW PROPERTIES LIST:</strong> Prerequisite — makes
              the Properties List card/tab visible at all.
            </li>
            <li>
              <strong>READ PROPERTY LIST:</strong> Can see the Property List
              and view property details, but no Delete option, and cannot
              edit property details.
            </li>
            <li>
              <strong>READ & WRITE PROPERTY LIST:</strong> Same as above,
              but can also delete and edit property details.
            </li>
            <li>
              <strong>SHOW RRC:</strong> Prerequisite — makes the RRC
              card/tab visible at all.
            </li>
            <li>
              <strong>SHOW RRC + SHOW REVENUE CALCULATOR:</strong>
              Additionally unlocks the Revenue Calculator option within
              RRC.
            </li>
            <li>
              <strong>SHOW RRC + PROPERTY WISE TARGETS:</strong>
              Additionally unlocks the Property Wise Targets option within
              RRC.
            </li>
            <li>
              <strong>READ PROPERTY-WISE TARGETS:</strong> Can only see the
              Property Targets List page, not the Revenue Calculator.
              Cannot delete a property from that list, and the pencil edit
              option is hidden after clicking View Targets.
            </li>
            <li>
              <strong>READ PROPERTY-WISE TARGETS & WRITE REVENUE
              CALCULATOR:</strong> Can also see and submit the Revenue
              Calculator; can delete on the Property Targets List; and the
              pencil edit option appears after clicking View Targets.
            </li>
            <li>
              <strong>SHOW DASHBOARDS:</strong> Prerequisite — makes the
              Dashboards card/tab visible at all.
            </li>
            <li>
              <strong>READ DASHBOARDS:</strong> Can view the Dashboards
              component on the Home Page.
            </li>
            <li>
              Any permission marked "SHOW" is a prerequisite — it must be
              granted before its matching READ/READ &amp; WRITE permission
              means anything, since it controls whether the section is
              visible at all.
            </li>
          </ul>
        </div>

      {/* View Logs modal — opened from a row's View Logs button in the
          Existing Users table above. position: fixed means this overlay
          works correctly regardless of where it sits in the component
          tree, so placing it here as the last child is safe. */}
      {viewLogsUser && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
          onClick={() => setViewLogsUser(null)}
        >
          <div
            style={{
              backgroundColor: theme === "dark" ? "#2a2a2a" : "#fff",
              borderRadius: 8,
              padding: 20,
              width: "90%",
              maxWidth: 640,
              maxHeight: "80vh",
              overflow: "auto",
              boxShadow: "0 10px 40px rgba(0, 0, 0, 0.25)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {(() => {
              const entry = getLogsGroupedByUser().find(
                (g) => g.username === viewLogsUser
              );

              return (
                <>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginBottom: 12,
                    }}
                  >
                    <h3 style={{ margin: 0, color: theme === "dark" ? "#fff" : "#073b4c" }}>
                      Logs for {viewLogsUser}
                      {entry?.role ? ` (${entry.role})` : ""}
                    </h3>
                    <button
                      type="button"
                      onClick={() => setViewLogsUser(null)}
                      aria-label="Close"
                      style={{
                        background: "none",
                        border: "none",
                        fontSize: 22,
                        lineHeight: 1,
                        cursor: "pointer",
                        color: theme === "dark" ? "#fff" : "#333",
                      }}
                    >
                      ×
                    </button>
                  </div>

                  {entry ? (
                    <UserLogTable
                      userGroup={entry}
                      logPageByUser={logPageByUser}
                      setLogPageByUser={setLogPageByUser}
                      logsPerPage={LOGS_PER_PAGE}
                    />
                  ) : (
                    <p style={{ color: theme === "dark" ? "#ccc" : "#777" }}>
                      No logs found for this user.
                    </p>
                  )}
                </>
              );
            })()}
          </div>
        </div>
      )}
    </div>
  </div>
);
}

export default SettingsPage;
